import {
  BoltIcon,
  EyeIcon,
  ArrowsPointingOutIcon,
  PencilSquareIcon,
  CubeTransparentIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  CodeBracketIcon,
  ServerStackIcon,
  SignalIcon,
  CurrencyDollarIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { EngineeringPageSEO } from '../seo/seo';

function EngineeringHero() {
  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-white/50 to-white/0 dark:from-gray-900/50 dark:to-gray-900/0">
      {/* Subtle background glow */}
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* Hero */}
      <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-32 text-center">
        <h1 className="animate-fadeUp text-4xl font-extrabold tracking-tight text-gray-900 sm:text-6xl dark:text-gray-100">
          Engineering,
          <span className="animate-fadeUp block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Done Properly
          </span>
        </h1>

        <p className="animate-fadeUp mx-auto mt-8 max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">
          We engineer AI systems and software platforms that survive real traffic, real users, and
          real failure modes. Architecture, reliability, and scale are not afterthoughts here.
        </p>
      </div>
    </section>
  );
}

function EngineeringPillars() {
  const pillars = [
    {
      title: 'System Architecture',
      desc: 'Clear boundaries, predictable data flow, and architectures designed to evolve without rewrites. We optimize for long-term clarity, not short-term speed.',
      accent: 'from-indigo-400/20',
    },
    {
      title: 'AI & Data Engineering',
      desc: 'From ingestion to inference, we design AI systems as operational pipelines. Reproducible, observable, and resilient under real-world conditions.',
      accent: 'from-purple-400/20',
    },
    {
      title: 'Production & Reliability',
      desc: 'Monitoring, failure recovery, versioning, and rollback are built in from day one. Systems are engineered to fail gracefully, not silently.',
      accent: 'from-pink-400/20',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl py-28">
      <div className="mx-auto max-w-7xl px-6">
        {/* Section header */}
        <motion.div
          className="mb-20 max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Foundations
          </motion.span>

          <motion.h2
            className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Engineering Pillars
          </motion.h2>

          <motion.p
            className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            These pillars shape how we design systems, make architecture decisions, and ship
            software that holds up in production.
          </motion.p>
        </motion.div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              className="group relative overflow-hidden rounded-3xl border border-gray-200/60 bg-white p-8 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1 + 0.4,
                ease: 'easeOut',
              }}
              whileHover={{
                y: -5,
                transition: { duration: 0.2 },
              }}
              viewport={{ once: true }}
            >
              {/* Always-on soft gradient wash */}
              <div className="pointer-events-none absolute inset-0">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${pillar.accent} via-transparent to-transparent opacity-60`}
                />
              </div>

              {/* Hover intensifier */}
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${pillar.accent} via-transparent to-transparent`}
                />
              </div>

              <motion.h3
                className="relative mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 + 0.6 }}
                viewport={{ once: true }}
              >
                {pillar.title}
              </motion.h3>

              <motion.p
                className="relative text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 + 0.7 }}
                viewport={{ once: true }}
              >
                {pillar.desc}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// function EngineeringPrinciples() {
//   const principles = [
//     {
//       title: 'Design for Failure',
//       desc: 'Every system fails. We assume it, model it, and design recovery paths before writing business logic.',
//       glow: 'from-red-400/30 via-orange-300/10',
//     },
//     {
//       title: 'Simple Before Clever',
//       desc: 'If a system cannot be explained on a whiteboard, it does not belong in production.',
//       glow: 'from-indigo-400/30 via-purple-300/10',
//     },
//     {
//       title: 'Observability Is Not Optional',
//       desc: 'Metrics, logs, and traces are first-class citizens. If you cannot see it, you cannot operate it.',
//       glow: 'from-emerald-400/30 via-teal-300/10',
//     },
//     {
//       title: 'Scale Is a Property',
//       desc: 'We treat scalability as a design constraint, not a future problem to postpone.',
//       glow: 'from-sky-400/30 via-blue-300/10',
//     },
//   ];

//   return (
//     <section className="relative mx-auto w-full max-w-6xl overflow-hidden py-32">
//       {/* Ambient background */}
//       <div className="pointer-events-none absolute inset-0">
//         <div className="absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/15 blur-3xl dark:bg-indigo-500/10" />
//       </div>

//       <div className="relative mx-auto max-w-7xl px-6">
//         {/* Header */}
//         <div className="mb-20 max-w-3xl">
//           <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
//             Operating Constraints
//           </span>

//           <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100">
//             Engineering Principles
//           </h2>

//           <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">
//             These are not slogans. They are constraints we refuse to violate, even when timelines
//             get uncomfortable.
//           </p>
//         </div>

//         {/* Principles grid */}
//         <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
//           {principles.map((p) => (
//             <div
//               key={p.title}
//               className="group relative overflow-hidden rounded-[2rem] border border-gray-200/70 bg-white p-10 shadow-sm backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-white/5"
//             >
//               {/* Soft gradient base (always on) */}
//               <div className="pointer-events-none absolute inset-0">
//                 <div
//                   className={`absolute inset-0 bg-gradient-to-br ${p.glow} to-transparent opacity-40`}
//                 />
//               </div>

//               {/* Hover intensifier */}
//               <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
//                 <div className={`absolute inset-0 bg-gradient-to-br ${p.glow} to-transparent`} />
//               </div>

//               {/* Edge definition */}
//               <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-black/5 dark:ring-white/10" />

//               <h3 className="relative mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
//                 {p.title}
//               </h3>

//               <p className="relative text-sm leading-relaxed text-gray-600 sm:text-base dark:text-gray-300">
//                 {p.desc}
//               </p>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// }

function EngineeringOutcomes() {
  interface Outcome {
    title: string;
    metric: string;
    desc: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    gradient: string;
  }

  const outcomes: Outcome[] = [
    {
      title: 'Predictable Systems',
      metric: '99.95% Uptime',
      desc: 'Architectures designed with clear failure boundaries and recovery paths result in systems that behave consistently under load.',
      icon: BoltIcon,
      gradient: 'from-indigo-400 via-purple-400 to-pink-400',
    },
    {
      title: 'Operational Clarity',
      metric: '< 5 min MTTR',
      desc: 'Observability-first design reduces incident resolution time by making failure modes obvious, not mysterious.',
      icon: EyeIcon,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      title: 'Controlled Scaling',
      metric: '10x Traffic Ready',
      desc: 'Systems scale horizontally without rewrites because scale was treated as a property from day one.',
      icon: ArrowsPointingOutIcon,
      gradient: 'from-yellow-400 via-orange-400 to-red-400',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl py-32">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <motion.div
          className="mb-20 max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Measurable Results
          </motion.span>

          <motion.h2
            className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Engineering Outcomes
          </motion.h2>

          <motion.p
            className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            This is what disciplined engineering produces in real environments, not slide decks.
          </motion.p>
        </motion.div>

        {/* Outcomes grid */}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          {outcomes.map((o, index) => {
            const Icon = o.icon;

            return (
              <motion.div
                key={o.title}
                className="group relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white p-8 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1 + 0.4,
                  ease: 'easeOut',
                }}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
                viewport={{ once: true }}
              >
                {/* Soft gradient wash */}
                <div className="pointer-events-none absolute inset-0 opacity-30">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${o.gradient} to-transparent`}
                  />
                </div>

                {/* Icon */}
                <motion.div
                  className={`relative mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${o.gradient} text-white shadow-md`}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1 + 0.6,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  viewport={{ once: true }}
                >
                  <Icon className="h-6 w-6" />
                </motion.div>

                {/* Metric */}
                <motion.div
                  className="relative mb-2 text-sm font-semibold tracking-wide text-indigo-600 dark:text-indigo-400"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.7 }}
                  viewport={{ once: true }}
                >
                  {o.metric}
                </motion.div>

                {/* Title */}
                <motion.h3
                  className="relative mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.8 }}
                  viewport={{ once: true }}
                >
                  {o.title}
                </motion.h3>

                {/* Description */}
                <motion.p
                  className="relative text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.9 }}
                  viewport={{ once: true }}
                >
                  {o.desc}
                </motion.p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowWeBuildTimeline() {
  const steps = [
    {
      title: 'Problem Definition',
      desc: 'We start by understanding constraints, risks, and operational realities. No solution exists until the problem is precisely defined.',
      icon: PencilSquareIcon,
      gradient: 'from-indigo-400 to-purple-400',
    },
    {
      title: 'System Design',
      desc: 'Architecture, data flow, and failure boundaries are designed before implementation. This is where most projects succeed or fail.',
      icon: CubeTransparentIcon,
      gradient: 'from-purple-400 to-pink-400',
    },
    {
      title: 'Implementation',
      desc: 'We build in small, verifiable units with strict boundaries. Simplicity beats cleverness every time.',
      icon: CpuChipIcon,
      gradient: 'from-pink-400 to-rose-400',
    },
    {
      title: 'Production Hardening',
      desc: 'Monitoring, alerting, rollback paths, and performance tuning are added before users ever touch the system.',
      icon: ChartBarIcon,
      gradient: 'from-emerald-400 to-teal-400',
    },
    {
      title: 'Launch & Iterate',
      desc: 'We ship, observe real behavior, and refine. Engineering continues after launch, not before it.',
      icon: RocketLaunchIcon,
      gradient: 'from-sky-400 to-blue-400',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl overflow-hidden py-32">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="bg-indigo-500/8 absolute left-1/2 top-1/3 h-[600px] w-[600px] -translate-x-1/2 rounded-full blur-3xl" />
        <div className="bg-fuchsia-500/8 absolute right-0 top-24 h-80 w-80 rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <motion.div
          className="mb-24 max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Our Process
          </motion.span>

          <motion.h2
            className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            How We Build
          </motion.h2>

          <motion.p
            className="mt-4 text-base text-gray-700 sm:text-lg dark:text-gray-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            A disciplined process designed to reduce risk, surface problems early, and produce
            systems that hold up in production.
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <motion.div
            className="absolute left-6 top-0 hidden h-full w-px bg-gradient-to-b from-indigo-500/30 via-purple-500/30 to-transparent md:block"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            viewport={{ once: true }}
            style={{ transformOrigin: 'top' }}
          />

          <div className="space-y-16">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  className="relative flex flex-col gap-6 md:flex-row md:items-start"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1 + 0.5,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  {/* Icon */}
                  <motion.div
                    className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-r text-white"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1 + 0.7,
                      type: 'spring',
                      stiffness: 200,
                    }}
                    whileHover={{ scale: 1.1 }}
                    viewport={{ once: true }}
                  >
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${step.gradient}`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                  </motion.div>

                  {/* Card */}
                  <motion.div
                    className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl md:ml-8 dark:border-white/10 dark:bg-white/5"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1 + 0.8,
                      ease: 'easeOut',
                    }}
                    whileHover={{
                      y: -5,
                      transition: { duration: 0.2 },
                    }}
                    viewport={{ once: true }}
                  >
                    {/* Top gradient wash */}
                    <div
                      className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${step.gradient} opacity-15 dark:opacity-20`}
                    />

                    {/* Divider line */}
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent dark:via-white/10" />

                    <motion.span
                      className="relative mb-2 block text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 + 1.0 }}
                      viewport={{ once: true }}
                    >
                      Step {index + 1}
                    </motion.span>

                    <motion.h3
                      className="relative mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 + 1.1 }}
                      viewport={{ once: true }}
                    >
                      {step.title}
                    </motion.h3>

                    <motion.p
                      className="relative text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 + 1.2 }}
                      viewport={{ once: true }}
                    >
                      {step.desc}
                    </motion.p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function ToolingStack() {
  const stackItems: {
    name: string;
    desc: string;
    icon: React.ElementType;
    gradient: string;
  }[] = [
    {
      name: 'MERN Stack',
      desc: 'Fast, scalable, and maintainable full-stack applications using MongoDB, Express, React, and Node.js.',
      icon: CodeBracketIcon,
      gradient: 'from-indigo-400 via-purple-400 to-pink-400',
    },
    {
      name: 'AI / ML Frameworks',
      desc: 'PyTorch, TensorFlow, and scikit-learn for building reliable and production-ready AI models.',
      icon: CpuChipIcon,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      name: 'Cloud & DevOps',
      desc: 'Azure, AWS, Docker, and Kubernetes to deploy, scale, and monitor systems efficiently.',
      icon: ServerStackIcon,
      gradient: 'from-orange-400 via-rose-400 to-pink-400',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl overflow-hidden py-32">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/3 top-0 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-indigo-500/5 blur-3xl" />
        <div className="absolute right-1/4 top-20 h-[300px] w-[300px] rounded-full bg-pink-500/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <motion.div
          className="mb-20 max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            What We Use
          </motion.span>

          <motion.h2
            className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Tooling & Stack
          </motion.h2>

          <motion.p
            className="mt-4 text-base text-gray-700 sm:text-lg dark:text-gray-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Our tech choices are deliberate. Every tool, framework, and service is selected for
            reliability, scale, and long-term maintainability.
          </motion.p>
        </motion.div>

        {/* Stack grid */}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {stackItems.map((tool, index) => {
            const Icon = tool.icon;
            return (
              <motion.div
                key={tool.name}
                className="group relative rounded-3xl border border-gray-200/50 bg-white/70 p-6 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-white/5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1 + 0.4,
                  ease: 'easeOut',
                }}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
                viewport={{ once: true }}
              >
                {/* Gradient icon */}
                <motion.div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${tool.gradient} text-white`}
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1 + 0.6,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  viewport={{ once: true }}
                >
                  <Icon className="h-6 w-6" />
                </motion.div>

                {/* Tool name */}
                <motion.h3
                  className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.7 }}
                  viewport={{ once: true }}
                >
                  {tool.name}
                </motion.h3>

                {/* Description */}
                <motion.p
                  className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.8 }}
                  viewport={{ once: true }}
                >
                  {tool.desc}
                </motion.p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  const industries = [
    {
      name: 'AI & ML',
      desc: 'Building intelligent systems and models that adapt, predict, and optimize across real-world scenarios.',
      icon: CpuChipIcon,
      gradient: 'from-indigo-400 via-purple-400 to-pink-400',
      link: '/ai-and-data',
    },
    {
      name: 'Telecom',
      desc: 'Enhancing connectivity, optimizing networks, and enabling new digital experiences at scale.',
      icon: SignalIcon,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
      link: '/engineering/telcom',
    },
    {
      name: 'FinTech',
      desc: 'Designing secure, reliable, and scalable financial platforms for modern businesses and consumers.',
      icon: CurrencyDollarIcon,
      gradient: 'from-yellow-400 via-orange-400 to-red-400',
      link: '/engineering/fintech',
    },
    {
      name: 'XR & Gaming',
      desc: 'Creating immersive virtual environments and interactive experiences with cutting-edge gaming technologies.',
      icon: ViewfinderCircleIcon,
      gradient: 'from-pink-400 via-rose-400 to-purple-400',
      link: '/engineering/gaming',
    },
  ];
  return (
    <section className="relative mx-auto w-full max-w-6xl overflow-hidden py-32">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial ambient glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.15),transparent_60%)]" />
        {/* Grid lines */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.06)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="relative mx-auto px-6">
        {/* Section header */}
        <motion.div
          className="mx-auto mb-20 max-w-3xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Our Impact
          </motion.span>

          <motion.h2
            className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Industries We Shape
          </motion.h2>

          <motion.p
            className="mt-4 text-base text-gray-700 sm:text-lg dark:text-gray-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            From AI to Edutech, we create solutions that drive transformation across industries.
          </motion.p>
        </motion.div>

        {/* Industry cards */}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <motion.article
                key={industry.name}
                className="group relative overflow-hidden rounded-3xl border border-gray-200/50 bg-white/80 p-8 backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-white/5"
                aria-labelledby={`industry-${industry.name}`}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1 + 0.4,
                  ease: 'easeOut',
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { duration: 0.2 },
                }}
                viewport={{ once: true }}
              >
                {/* Accent gradient blur */}
                <motion.div
                  className={`absolute -left-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-tr ${industry.gradient} pointer-events-none opacity-20 blur-3xl`}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: index * 0.1 + 0.5,
                  }}
                  viewport={{ once: true }}
                />

                {/* Icon */}
                <motion.div
                  className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r ${industry.gradient} text-white shadow-lg transition-all duration-300 group-hover:scale-110`}
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1 + 0.6,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  whileHover={{
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.2 },
                  }}
                  viewport={{ once: true }}
                >
                  <Icon className="h-7 w-7" />
                </motion.div>

                {/* Title */}
                <motion.h3
                  id={`industry-${industry.name}`}
                  className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.7 }}
                  viewport={{ once: true }}
                >
                  {industry.name}
                </motion.h3>

                {/* Description */}
                <motion.p
                  className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.8 }}
                  viewport={{ once: true }}
                >
                  {industry.desc}
                </motion.p>

                {/* Optional CTA for SEO & engagement */}
                <motion.a
                  href={industry.link || '#'}
                  className="mt-4 inline-block text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                  aria-label={`Learn more about ${industry.name}`}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.9 }}
                  whileHover={{ x: 5 }}
                  viewport={{ once: true }}
                >
                  Learn More →
                </motion.a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProjectsSection() {
  const projects = [
    {
      name: 'Free Data Platform',
      description: `Our recent telco collaboration with Dialog Axiata PLC, during the Vesak and Poson seasons, allowed users across Colombo and Anuradhapura to enjoy free mobile data simply by scanning QR codes at selected locations in Sri Lanka.`,
      color: 'from-emerald-400 via-teal-400 to-cyan-400',
      image: '/Images/Projects/datadansala.png', // replace with real screenshot
      link: 'https://www.dialog.lk/',
    },
    // ...other products
  ];

  return (
    <section className="mx-auto max-w-6xl px-6 py-28">
      {/* Section Header */}
      <div className="mx-auto mb-5 text-left">
        <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100">
          Projects We’ve Built
        </h2>
        <p className="mt-4 leading-relaxed text-gray-600 sm:text-lg md:text-xl dark:text-gray-300">
          Cutting-edge AI, fintech, and telco solutions we’ve built for clients across industries,
          streamlining workflows, driving measurable impact, and delivering real-world results.
        </p>
      </div>

      {/* Projects */}
      <div className="relative w-full py-6">
        {projects.map((project) => (
          <div
            key={project.name}
            className={`group relative mb-12 flex flex-col overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-tr from-blue-500/30 via-indigo-500/30 to-purple-500/30 p-6 shadow-2xl backdrop-blur-xl transition-all duration-500 hover:scale-105 lg:flex-row`}
          >
            {/* Left side: Image */}
            <div className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white lg:w-1/2">
              <img
                src={project.image}
                alt={`${project.name} Screenshot`}
                className="h-80 w-auto rounded-xl object-contain"
              />
            </div>

            {/* Right side: Content */}
            <div className="mt-6 flex flex-col justify-center lg:ml-12 lg:mt-0 lg:w-1/2">
              <h3 className="mb-4 text-3xl font-extrabold text-white drop-shadow-md sm:text-4xl">
                {project.name}
              </h3>
              <p className="mb-6 text-lg leading-relaxed text-white/90 sm:text-xl">
                {project.description}
              </p>
              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block rounded-full bg-white/20 px-8 py-3 font-medium text-white backdrop-blur-lg transition hover:bg-white/30"
                >
                  Learn More
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Engineering() {

  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <EngineeringPageSEO />
      <EngineeringHero />
      <EngineeringPillars />
      <IndustriesSection />
      {/* <EngineeringPrinciples /> */}
      <HowWeBuildTimeline />
      <ToolingStack />
      <EngineeringOutcomes />
      <ProjectsSection />
    </div>
  );
}
